// ./client/src/SlateEditor.tsx
import React, { useState, useCallback, useMemo, useEffect, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import {
    createEditor,
    Descendant,
    Text,
    Transforms,
    Editor,
    Element as SlateElement,
    Node,
    BaseEditor,
    Range,
} from 'slate';
import { withHistory, HistoryEditor } from 'slate-history';
import {
    Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, Palette
} from 'lucide-react';

// Import utility functions and types from slateUtils.ts
import {
    CustomEditor as SlateCustomEditor,
    CustomElement, CustomText, ParagraphElement,
    deserializeHTML, serializeHTML,
    toggleMark, isMarkActive,
    toggleAlignment, getCurrentAlignment,
    setColorMark, getCurrentColor,
    getWordCount, isEditorEmpty
} from './slateUtils.ts'; // Adjust path if needed

// Import configuration settings
import { TEXT_COLOR_MODE } from './config'; // Adjust path if needed

// Define the props for the SlateEditor component
interface SlateEditorProps {
    initialHtml?: string;
    onChange: (html: string) => void; // Ensure this prop is stable (e.g., defined with useCallback in parent)
    selectedThemeColor: string;
    placeholder?: string;
}

// Define available text colors for the custom color picker
const availableColors = [
    { name: 'purple', color: '#9333ea' }, { name: 'blue', color: '#2563eb' },
    { name: 'green', color: '#16a34a' }, { name: 'red', color: '#dc2626' },
    { name: 'amber', color: '#d97706' }, { name: 'pink', color: '#db2777' },
    { name: 'teal', color: '#0d9488' }, { name: 'indigo', color: '#4f46e5' }
];

// Helper to check if running on Android
const IS_ANDROID = typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);

/**
 * Production-ready rich text editor component using Slate.js with fixes for
 * Android IME issues. V6: Disables native corrections for all devices.
 */
const SlateEditorComponent: React.FC<SlateEditorProps> = ({
    initialHtml = '',
    onChange, // Parent should memoize this callback if possible
    selectedThemeColor,
    placeholder = "What's on your mind?",
}) => {
    const editor = useMemo(() => withHistory(withReact(createEditor() as BaseEditor & ReactEditor & HistoryEditor)), []);

    const editorContainerRef = useRef<HTMLDivElement>(null);
    const colorPickerRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const isComposingRef = useRef(false);
    const needsOnChangeRef = useRef(false);

    const [value, setValue] = useState<Descendant[]>(() => deserializeHTML(initialHtml));
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [textColorPickerOpen, setTextColorPickerOpen] = useState<boolean>(false);
    const [selectionState, setSelectionState] = useState(editor.selection);
    const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);

    const typedEditor = editor as SlateCustomEditor;

    const effectiveToolbarColor = useMemo(() => {
        switch (TEXT_COLOR_MODE) {
            case 'selected': return selectedThemeColor;
            case 'white': return 'white';
            case 'custom': default: return getCurrentColor(typedEditor);
        }
    }, [TEXT_COLOR_MODE, selectedThemeColor, typedEditor, selectionState]);

    // --- Effects --- (Remain the same)
    useEffect(() => {
        let forcedColor: string | null = null;
        if (TEXT_COLOR_MODE === 'selected') forcedColor = selectedThemeColor;
        else if (TEXT_COLOR_MODE === 'white') forcedColor = 'white';

        if (forcedColor) {
            Editor.withoutNormalizing(editor, () => {
                Transforms.setNodes<Text>(editor, { color: forcedColor! }, { match: Text.isText, at: [] });
                if (editor.selection) setColorMark(typedEditor, forcedColor!);
            });
            if (!isComposingRef.current) {
                onChange(serializeHTML(editor.children));
            } else {
                needsOnChangeRef.current = true;
            }
            setSelectionState(editor.selection);
        }
    }, [TEXT_COLOR_MODE, selectedThemeColor, editor, onChange, typedEditor]);

    useEffect(() => {
        const container = editorContainerRef.current;
        if (!container) return;
        container.classList.toggle('focused', isFocused);
        container.classList.toggle('has-content', !isEditorEmpty(value));
    }, [isFocused, value]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (textColorPickerOpen &&
                colorPickerRef.current && !colorPickerRef.current.contains(event.target as Node) &&
                popoverRef.current && !popoverRef.current.contains(event.target as Node)
               ) {
                setTextColorPickerOpen(false);
                setPopoverPosition(null);
            }
        };
        if (textColorPickerOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [textColorPickerOpen]);

    // --- Rendering Logic --- (Remain the same)
    const renderElement = useCallback((props: any) => {
        const { attributes, children, element } = props;
        const style: React.CSSProperties = {};
        if ((element as ParagraphElement).align) {
             style.textAlign = (element as ParagraphElement).align;
        }
        switch ((element as CustomElement).type) {
             case 'paragraph':
                 return <p style={style} {...attributes}>{children}</p>;
             default:
                  return <div style={style} {...attributes}>{children}</div>;
        }
    }, []);

    const renderLeaf = useCallback((props: any) => {
        let { attributes, children, leaf } = props;
        const leafStyle: React.CSSProperties = {};
        let displayColor = 'inherit';
        if (TEXT_COLOR_MODE === 'selected') displayColor = selectedThemeColor;
        else if (TEXT_COLOR_MODE === 'white') displayColor = 'white';
        else if (leaf.color) displayColor = leaf.color;
        if (displayColor !== 'inherit') leafStyle.color = displayColor;
        if (leaf.bold) children = <strong>{children}</strong>;
        if (leaf.italic) children = <em>{children}</em>;
        if (leaf.underline) children = <u>{children}</u>;
        return <span {...attributes} style={leafStyle}>{children}</span>;
    }, [TEXT_COLOR_MODE, selectedThemeColor]);

    // --- Event Handlers ---

    // Memoized handler for Slate value changes
    const handleValueChange = useCallback((newValue: Descendant[]) => {
        // console.log("handleValueChange triggered. Composing:", isComposingRef.current);
        setValue(newValue); // Update local state

        const isAstChange = editor.operations.some(op => op.type !== 'set_selection');

        // Update selection state used by toolbar
        if (!selectionState || !editor.selection || !Range.equals(selectionState, editor.selection)) {
            setSelectionState(editor.selection);
        }

        // Propagate change to parent if content changed and not composing
        if (isAstChange) {
            if (!isComposingRef.current) {
                // console.log("--> Not Composing: Calling onChange");
                onChange(serializeHTML(newValue));
                needsOnChangeRef.current = false; // Reset pending flag
            } else {
                // console.log("--> Composing: Setting needsOnChangeRef=true");
                needsOnChangeRef.current = true; // Flag change to be sent later
            }
        }

    }, [editor, onChange, selectionState]); // Dependencies

    // Memoized handler for the native DOM beforeinput event
    const handleDOMBeforeInput = useCallback((event: InputEvent) => {
        // console.log('handleDOMBeforeInput:', event.inputType, event.data, 'isComposing:', isComposingRef.current);

        // --- Targeted Fix for Android Spacebar Issue ---
        if (event.inputType === 'insertText' && event.data === ' ' && IS_ANDROID) {
             // console.log("--> Spacebar on Android: Preventing Default & Inserting Text");
             event.preventDefault(); // Stop the browser's conflicting action
             // Use static Editor method for clarity
             Editor.insertText(editor, ' '); // Manually insert the space via Slate

             // Ensure change is propagated correctly, respecting composition state
             if (isComposingRef.current) {
                needsOnChangeRef.current = true;
             } else {
                onChange(serializeHTML(editor.children));
                needsOnChangeRef.current = false;
             }
             return; // Handled
        }
        // Allow slate-react's default handler for other events
    }, [editor, onChange]); // Dependencies

    // Memoized handler for Composition Start
    const handleCompositionStart = useCallback(() => {
        // console.log("handleCompositionStart");
        isComposingRef.current = true;
        needsOnChangeRef.current = false; // Reset pending flag
    }, []);

    // Memoized handler for Composition End
    const handleCompositionEnd = useCallback(() => {
        // console.log("handleCompositionEnd. Needs onChange:", needsOnChangeRef.current);
        isComposingRef.current = false;
        // Use a slightly longer delay for Android IME to settle
        const delay = IS_ANDROID ? 150 : 50; // Keep increased Android delay
        setTimeout(() => {
            if (needsOnChangeRef.current) {
                // console.log(`--> Composition End Timeout (${delay}ms): Calling onChange`);
                onChange(serializeHTML(editor.children));
                needsOnChangeRef.current = false; // Reset the flag
            }
        }, delay);

    }, [editor, onChange]); // Dependencies

    // --- Other Handlers ---
    const handleColorButtonClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setTextColorPickerOpen(prev => !prev);
        if (!textColorPickerOpen) {
             const buttonContainerRect = colorPickerRef.current?.getBoundingClientRect();
             if (buttonContainerRect) {
                 setPopoverPosition({ top: buttonContainerRect.bottom + window.scrollY + 8, left: buttonContainerRect.left + window.scrollX });
             }
        } else {
            setPopoverPosition(null);
        }
    }, [textColorPickerOpen]);

    const handleColorSelection = useCallback((color: string) => {
         setColorMark(typedEditor, color);
         setTextColorPickerOpen(false);
         setPopoverPosition(null);
         handleValueChange(editor.children);
     }, [editor, typedEditor, handleValueChange]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (isComposingRef.current) return;
        if (!event.ctrlKey) return;
        switch (event.key) {
            case 'b': event.preventDefault(); toggleMark(typedEditor, 'bold'); break;
            case 'i': event.preventDefault(); toggleMark(typedEditor, 'italic'); break;
            case 'u': event.preventDefault(); toggleMark(typedEditor, 'underline'); break;
        }
    }, [typedEditor]);

    const currentAlignment = getCurrentAlignment(typedEditor);
    const wordCount = getWordCount(value);

    // --- Render Component ---
    return (
        <div ref={editorContainerRef} className="slate-editor-container border border-gray-600 rounded-md bg-gray-700">
            <Slate editor={editor} initialValue={value} onChange={handleValueChange}>
                {/* Toolbar */}
                <div className="formatting-toolbar bg-gray-700 p-1 rounded-t border-b border-gray-600 flex flex-nowrap items-center gap-1 overflow-x-auto overflow-y-visible">
                    {/* Toolbar Groups... */}
                    <div className="formatting-group flex items-center border-r border-gray-600 pr-1">
                        <button type="button" className={`formatting-button ${isMarkActive(typedEditor, 'bold') ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); toggleMark(typedEditor, 'bold'); }} title="Bold (Ctrl+B)"><Bold size={16} /></button>
                        <button type="button" className={`formatting-button ${isMarkActive(typedEditor, 'italic') ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); toggleMark(typedEditor, 'italic'); }} title="Italic (Ctrl+I)"><Italic size={16} /></button>
                        <button type="button" className={`formatting-button ${isMarkActive(typedEditor, 'underline') ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); toggleMark(typedEditor, 'underline'); }} title="Underline (Ctrl+U)"><Underline size={16} /></button>
                    </div>
                    <div className="formatting-group flex items-center border-r border-gray-600 px-1">
                        <button type="button" className={`formatting-button ${currentAlignment === 'left' ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); toggleAlignment(typedEditor, 'left'); }} title="Align Left"><AlignLeft size={16} /></button>
                        <button type="button" className={`formatting-button ${currentAlignment === 'center' ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); toggleAlignment(typedEditor, 'center'); }} title="Align Center"><AlignCenter size={16} /></button>
                        <button type="button" className={`formatting-button ${currentAlignment === 'right' ? 'active' : ''}`} onMouseDown={e => { e.preventDefault(); toggleAlignment(typedEditor, 'right'); }} title="Align Right"><AlignRight size={16} /></button>
                    </div>
                    {TEXT_COLOR_MODE === 'custom' && (
                        <div ref={colorPickerRef} className="formatting-group relative text-color-picker-container flex items-center flex-shrink-0 pl-1">
                            <button type="button" className="formatting-button" onMouseDown={handleColorButtonClick} title="Text Color">
                                <div className="w-4 h-4 rounded-full border border-gray-500" style={{ backgroundColor: effectiveToolbarColor }}></div>
                            </button>
                        </div>
                    )}
                </div>

                {/* Editable Area with Native Corrections Disabled */}
                <Editable
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    placeholder={placeholder}
                    // --- Disable Native Corrections ---
                    spellCheck={false}
                    autoCorrect="off"
                    autoCapitalize="none"
                    inputMode="text" // Keep explicit inputMode
                    // ---------------------------------
                    className="w-full bg-gray-700 rounded-b p-3 h-48 overflow-y-auto focus:outline-none slate-editor text-white"
                    style={{ minHeight: '180px' }}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    // Pass memoized event handlers
                    onDOMBeforeInput={handleDOMBeforeInput}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                    onKeyDown={handleKeyDown}
                />
            </Slate>

            {/* Color Picker Popover Portal */}
            {textColorPickerOpen && popoverPosition && createPortal(
                <div
                    ref={popoverRef}
                    className="text-color-picker bg-gray-800 border border-gray-700 rounded-md shadow-lg p-2 grid grid-cols-4 gap-2 z-50 w-auto"
                    style={{ position: 'absolute', top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px` }}
                     onMouseDown={e => e.preventDefault()} // Prevent blur when clicking popover
                >
                    {/* Color options... */}
                     <div className={`text-color-palette border-gray-400 cursor-pointer w-5 h-5 rounded-full border-2 ${effectiveToolbarColor === 'white' || effectiveToolbarColor === 'rgb(255, 255, 255)' ? 'border-white' : 'border-transparent'} justify-self-center`} style={{ backgroundColor: 'white' }} onClick={() => handleColorSelection('white')} title="White"></div>
                    {availableColors.map(color => (
                        <div key={color.name} className={`text-color-palette cursor-pointer w-5 h-5 rounded-full border-2 ${effectiveToolbarColor === color.color ? 'border-white' : 'border-transparent'} justify-self-center`} style={{ backgroundColor: color.color }} onClick={() => handleColorSelection(color.color)} title={color.name.charAt(0).toUpperCase() + color.name.slice(1)}></div>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
};

// Wrap the component with React.memo for performance optimization
export const SlateEditor = memo(SlateEditorComponent);
export default SlateEditor;


// --- Editor Specific Styles --- (Remain the same)
if (!document.getElementById('slate-editor-styles')) {
    const editorStyles = document.createElement('style');
    editorStyles.id = 'slate-editor-styles';
    editorStyles.textContent = `
    /* Styles identical to previous version */
    /* Container */
    .slate-editor-container { position: relative; transition: border-color 0.2s ease; }
    .slate-editor-container.focused { border-color: #6b7280; }
    /* Toolbar */
    .formatting-toolbar { display: flex; flex-wrap: nowrap; align-items: center; gap: 4px; overflow-x: auto; overflow-y: visible; scrollbar-width: thin; scrollbar-color: #4b5563 #374151; padding-bottom: 4px; }
    .formatting-toolbar::-webkit-scrollbar { height: 6px; }
    .formatting-toolbar::-webkit-scrollbar-track { background: #374151; border-radius: 3px; }
    .formatting-toolbar::-webkit-scrollbar-thumb { background-color: #4b5563; border-radius: 3px; }
    /* Toolbar Buttons */
    .formatting-button { background-color: transparent; border: none; color: #d1d5db; border-radius: 4px; padding: 6px; cursor: pointer; transition: background-color 0.2s ease, color 0.2s ease; display: flex; align-items: center; justify-content: center; margin: 0 1px; flex-shrink: 0; }
    .formatting-button:hover { background-color: #4b5563; color: white; }
    .formatting-button.active { background-color: #6b7280; color: white; }
    .formatting-button:focus { outline: none; }
    .formatting-button:focus-visible { outline: 2px solid #2563eb; outline-offset: 1px; }
    /* Toolbar Groups */
    .formatting-group { display: flex; align-items: center; flex-shrink: 0; }
    /* Color Picker */
    .text-color-picker-container { position: relative; }
    .text-color-picker { background-color: #1f2937; border: 1px solid #374151; border-radius: 0.375rem; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05); padding: 0.5rem; display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0.5rem; z-index: 1000; width: auto; min-width: 6rem; }
    .text-color-palette { transition: transform 0.1s ease, border-color 0.1s ease; justify-self: center; width: 1.25rem; height: 1.25rem; border-radius: 9999px; border-width: 2px; }
    .text-color-palette:hover { transform: scale(1.15); }
    /* Editable Area */
    .slate-editor { line-height: 1.6; outline: none; white-space: pre-wrap; word-wrap: break-word; caret-color: white; padding: 0.75rem; }
    .slate-editor:focus { outline: none; }
    /* Placeholder */
    .slate-editor [data-slate-placeholder="true"] { position: absolute; top: 0.75rem; left: 0.75rem; pointer-events: none; opacity: 0.5; color: #9ca3af; display: block; width: calc(100% - 1.5rem); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; -webkit-user-select: none; user-select: none; }
    .slate-editor-container.focused .slate-editor [data-slate-placeholder="true"], .slate-editor-container.has-content .slate-editor [data-slate-placeholder="true"] { display: none !important; }
    `;
    document.head.appendChild(editorStyles);
}
